import { validateCreationFields } from "../../../_shared/_logic/work-order-creation"
import {
  BATCH_PLANNING_MAX_ROWS,
  validateDemoBatchPlanningPropertyLimit,
} from "../_logic/planning-payloads"
import { validateBatchUploadPropertyLimit } from "../../../_shared/_logic/batch-upload-limits"
import type { LoadListOutput } from "../../../_shared/_logic/batch-upload-contract"
import { buildLoadListOutput, type BuildLoadListOutputInput } from "../_logic/output"
import type { CompleteLoadListIntent } from "../actions/completeLoadList"

export type BuildCompleteLoadListIntentInput = {
  locked: boolean
  existingOutput?: LoadListOutput
  unresolvedBacklogCount: number
  missingDbCount: number
  pendingUnitSelectionCount: number
  pendingUnitSelectionReferences?: string
  planningRowCount?: number
  trialRestrictionsActive?: boolean | null
  outputInput: BuildLoadListOutputInput
}

const blockedIntent = (label: string, message: string): CompleteLoadListIntent => ({
  kind: "blocked",
  label,
  disabled: true,
  disabledReason: message,
  message,
})

export const buildCompleteLoadListIntent = ({
  locked,
  existingOutput,
  unresolvedBacklogCount,
  missingDbCount,
  pendingUnitSelectionCount,
  pendingUnitSelectionReferences,
  planningRowCount,
  trialRestrictionsActive,
  outputInput,
}: BuildCompleteLoadListIntentInput): CompleteLoadListIntent => {
  if ((planningRowCount ?? 0) > BATCH_PLANNING_MAX_ROWS) {
    return blockedIntent(
      "Maximal 10.000 Planungszeilen",
      "Bitte reduzieren Sie diesen Durchgang auf maximal 10.000 Planungszeilen.",
    )
  }

  const propertyLimitError = validateBatchUploadPropertyLimit(
    locked && existingOutput
      ? existingOutput.selectedRows.map((row) => row.propertyId)
      : outputInput.selectedPropertyIds,
  )

  if (propertyLimitError) return blockedIntent("Maximal 500 Liegenschaften", propertyLimitError)

  if (locked) {
    if (existingOutput) {
      return {
        kind: "continue",
        label: "Weiter zu Termine planen",
        disabled: false,
        disabledReason: null,
      }
    }

    return blockedIntent("Weiter zu Termine planen", "Dieser Schritt ist bereits abgeschlossen.")
  }

  if (unresolvedBacklogCount > 0) {
    return blockedIntent(
      "Bitte Rückstandsliste klären",
      "Bitte offene Rückstandslisten-Zeilen einer Liegenschaft zuordnen oder bewusst entfernen.",
    )
  }

  if (!outputInput.selectedPropertyIds.length) {
    return blockedIntent(
      "Bitte Liegenschaften auswählen",
      "Bitte mindestens eine Liegenschaft auswählen.",
    )
  }

  const demoLimitError = validateDemoBatchPlanningPropertyLimit(
    planningRowCount ?? outputInput.selectedPropertyIds.length,
    {
      trialRestrictionsActive,
    },
  )

  if (demoLimitError) return blockedIntent("Maximal 10 Aufträge", demoLimitError)

  if (missingDbCount > 0) {
    return blockedIntent(
      "Bitte fehlende Liegenschaften anlegen",
      "Bitte alle fehlenden Liegenschaften anlegen, bevor es weitergeht.",
    )
  }

  if (pendingUnitSelectionCount > 0) {
    const propertyLabel = pendingUnitSelectionCount === 1 ? "Liegenschaft" : "Liegenschaften"

    return blockedIntent(
      "Bitte Einheiten auswählen",
      pendingUnitSelectionReferences
        ? `Bitte die Einheitsauswahl klären: ${pendingUnitSelectionReferences}.`
        : `Bitte für ${pendingUnitSelectionCount} ${propertyLabel} Gebäude- oder Wohneinheit wählen.`,
    )
  }

  const output = buildLoadListOutput(outputInput)

  if (!output.selectedRows.length) {
    return blockedIntent(
      "Bitte Einheiten auswählen",
      "Bitte mindestens eine Gebäude- oder Wohneinheit auswählen.",
    )
  }

  const invalid = output.selectedRows.find(
    (row) => !row.orderFields || validateCreationFields(row.orderFields),
  )

  if (invalid)
    return blockedIntent(
      "Bitte Auftragsangaben prüfen",
      invalid.orderFields
        ? validateCreationFields(invalid.orderFields)!
        : "Die Auftragsangaben fehlen.",
    )

  return {
    kind: "complete",
    label: `Aufträge anlegen und weiter (${output.selectedRows.length})`,
    disabled: false,
    disabledReason: null,
    output,
  }
}
